/**
 * Список подання з метаданих `/meta` над API записів (LST-01, E4a-W1): курсорний запит сторінок
 * `POST /api/v1/records/{entity}/query` і опис рядків для ядра таблиці (`features/list/RecordTable.tsx`).
 * Код знає лише типи полів реєстру (FLD-01), а не сутності (ADR-0003, розд. 5): колонки, підписи,
 * тони, назва запису — з метаданих. Поле, якого людина не бачить, у `/meta` не приходить, тож і
 * колонки немає (SEC-043).
 *
 * Умова подання (E4a-W4): сервер конфігурації не підставляє, тож запит несе діючу умову — чернетку
 * людини з адреси (`?d=`) чи пам'яті вкладки, а без неї — `config.filter` подання (view-config.ts).
 * Значення умов ідуть у тілі, а не в адресі запиту (SEC-110).
 *
 * Що людина змінила в списку сама: стовпці, їхній порядок і ширини (LST-02, LST-03) — особистий шар
 * подання на сервері (`view.prefs`, view-prefs.ts, E4a-03); сортування з шапки (LST-10) —
 * незбережена зміна подання в адресі (`?d=`, view-draft.ts, E4a-W3).
 */
import type {
  FieldValue,
  FilterNode,
  MetaEntity,
  MetaField,
  MetaView,
  RecordRow,
  ViewSort,
} from '@clientline/contracts';
import { infiniteQueryOptions, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { type RefObject, useMemo } from 'react';
import { loadRouteCode } from '../app/preload.ts';
import { OptionBadge } from '../features/list/badge.tsx';
import { type ColumnSpec, clampWidth, columnSpecs } from '../features/list/columns.ts';
import type { ListSource } from '../features/list/RecordTable.tsx';
import { formatPhone } from '../i18n/format.ts';
import { mark, PERF_MARKS } from '../perf/marks.ts';
import type { Prefs } from '../prefs/prefs.ts';
import { fetchQueryPage, platformKeys } from './api.ts';
import { LookupCell } from './LookupValue.tsx';
import { historyState, type ListContext } from './list-context.ts';
import { isLookup } from './lookup-refs.ts';
import { display, fieldOf, toneOf } from './meta.ts';
import { isStageField, STAGE_FIELDS, stageTone } from './pipeline.ts';
import { queryPolicy } from './query-policy.ts';
import { StageBadge } from './StageBadge.tsx';
import { platformText as t } from './text.ts';
import type { ViewQueryConfig } from './view-config.ts';
import type { ViewDraft } from './view-draft.ts';

/** Стовпець подання: поле і пріоритет показу (`MetaView.columns`). */
type ViewColumn = MetaView['columns'][number];

/**
 * Що людина змінила в поданні: `columns` — її стовпці після поля-назви, `widths` — її ширини
 * стовпців (особистий шар), `sort` — сортування з шапки (чернетка в адресі). Немає — як у поданні.
 */
export interface ListParams {
  columns?: readonly string[] | undefined;
  widths?: Readonly<Record<string, number>> | undefined;
  sort?: ViewSort | undefined;
  /**
   * Діюча умова (E4a-W4): чернетка людини, а без неї — умова подання; `null` — без умови. Немає —
   * умова невідома (лише старі виклики): запит без умови.
   */
  filter?: FilterNode | null | undefined;
  /** Пошук у поданні (SRC-15): через І з умовою; без сортування людини — за релевантністю. */
  q?: string | undefined;
}

/** Старі особисті стовпці подання з пристрою (`prefs.listColumns`); `undefined` — немає. */
export function personalColumns(
  entries: readonly string[],
  view: string,
): readonly string[] | undefined {
  const entry = entries.find((e) => e.startsWith(`${view}=`));
  if (entry === undefined) return undefined;
  const list = entry.slice(view.length + 1);
  return list === '' ? [] : list.split(',');
}

/** Новий `prefs.listColumns`: `undefined` — прибрати запис подання (після перенесення). */
export function withPersonalColumns(
  entries: readonly string[],
  view: string,
  columns: readonly string[] | undefined,
  max: number,
): string[] {
  const rest = entries.filter((e) => !e.startsWith(`${view}=`));
  if (columns === undefined) return rest;
  return [...rest, `${view}=${columns.join(',')}`].slice(-max);
}

/**
 * Що людина змінила в поданні — з особистого шару сервера (`view.prefs`, E4a-03) і чернетки з
 * адреси (`?d=`, view-draft.ts), і для `loader`, і для екрана. Старий вибір з пристрою
 * (`prefs.listColumns`) діє, лише доки його не перенесено на сервер (`migrateLocalColumns`).
 * Сортування за полем, яке вже не сортоване чи зникло з метаданих, відкидаємо (інакше 422).
 */
export function listParamsOf(
  view: MetaView,
  entity: MetaEntity,
  prefs: Pick<Prefs, 'listColumns'>,
  draft: ViewDraft = {},
  config?: ViewQueryConfig,
): ListParams {
  const columns = view.prefs?.columns ?? personalColumns(prefs.listColumns, view.key);
  const widths = view.prefs?.widths;
  const sort = draft.sort && isSortable(entity, draft.sort.field) ? draft.sort : undefined;
  const filter =
    draft.filter !== undefined ? draft.filter : config ? (config.filter ?? null) : undefined;
  return {
    ...(columns ? { columns } : {}),
    ...(widths && Object.keys(widths).length > 0 ? { widths } : {}),
    ...(sort ? { sort } : {}),
    ...(filter !== undefined ? { filter } : {}),
    ...(draft.q ? { q: draft.q } : {}),
  };
}

/** Стовпці подання з особистим вибором: пріоритет поля — з подання, нове поле — 1. */
export function effectiveColumns(view: MetaView, params: ListParams): readonly ViewColumn[] {
  if (!params.columns) return view.columns;
  return params.columns.map((field) => view.columns.find((c) => c.field === field) ?? { field });
}

/** Чи можна сортувати за полем: воно є в метаданих і позначене сортованим (LST-10, LST-16). */
export const isSortable = (entity: MetaEntity, key: string) =>
  fieldOf(entity, key)?.flags.includes('sortable') === true;

/** Сортування запиту: з шапки (уже перевірене `listParamsOf`), інакше — подання. */
export const listSort = (view: MetaView, params: ListParams): ViewSort => params.sort ?? view.sort;

/**
 * Запит сторінки: умова, сортування, поля — назва й стовпці, які є в метаданих (поле, якого людина
 * не бачить, сервер відхилив би).
 */
function listRequest(view: MetaView, entity: MetaEntity, params: ListParams) {
  const sort = params.q && !params.sort ? undefined : listSort(view, params);
  const keys = [...entity.titleFields, ...effectiveColumns(view, params).map((c) => c.field)];
  const fields = [...new Set(keys)].filter((key) => fieldOf(entity, key) !== undefined);
  return { sort, fields, filter: params.filter ?? undefined, q: params.q };
}

/** Порція списку тим самим запитом, що й таблиця (опитування живого оновлення, LST-18). */
export const fetchListPage = (
  view: MetaView,
  entity: MetaEntity,
  pageSize: number,
  params: ListParams,
  signal?: AbortSignal,
  cursor?: string,
) =>
  fetchQueryPage(
    view.entity,
    { ...listRequest(view, entity, params), viewId: view.id },
    pageSize,
    signal,
    cursor,
  );

/** Ключ кешу умови: однакові дерева — той самий рядок. */
const filterKey = (filter: FilterNode | null | undefined) => (filter ? JSON.stringify(filter) : '');

/**
 * Сторінки подання: курсор — непрозорий рядок сервера (keyset, LST-14), розмір — з профілю
 * можливостей (50, легкий режим 25). Ключ — без розміру: зміна режиму не скидає завантажене;
 * сортування, поля й умова — в ключі. Поки після зміни сортування, стовпців чи умови іде нова перша
 * порція, таблиця показує старі рядки (`keepPreviousData`, затемнені, FLT-18), а не скелетон. Новий
 * запит скасовує попередній (сигнал запиту без спостерігача), а запізніла відповідь лягає під свій
 * ключ і новішої не перезаписує.
 * Повернення до списку протягом 5 хв — з кешу без запиту (UX-08): зміни записів приносить SSE
 * (events.ts інвалідовує списки сутності; список на екрані оновлює живе оновлення, LST-18).
 */
export const platformListQuery = (
  view: MetaView,
  entity: MetaEntity,
  pageSize: number,
  params: ListParams = {},
) => {
  const request = listRequest(view, entity, params);
  return infiniteQueryOptions({
    queryKey: [
      ...platformKeys.list(view.entity, view.scope),
      request.sort ? `${request.sort.field}:${request.sort.dir}` : 'relevance',
      request.fields.join(','),
      filterKey(request.filter),
      // Текст пошуку — лише в пам'яті кешу цього запиту, як і значення фільтра.
      request.q ?? '',
    ],
    queryFn: ({ pageParam, signal }) =>
      fetchListPage(view, entity, pageSize, params, signal, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor,
    ...queryPolicy.list,
    placeholderData: keepPreviousData,
  });
};

/** Адреса картки платформи — як у маршруті `/r/$entity/$recordId` (app/routes/record.ts). */
export const cardHref = (entity: string, id: string) =>
  `/r/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`;

/** Порожнє значення в списку — порожня клітинка, без «—» (LST-07). */
const isEmpty = (value: FieldValue | undefined) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

/** Назва запису на екрані: поля заголовка через « — » (metadata-ui.md, розд. 3). */
export function rowTitle(entity: MetaEntity, row: RecordRow): string {
  const parts: string[] = [];
  for (const key of entity.titleFields) {
    const value = row.values[key];
    if (!isEmpty(value)) parts.push(display(fieldOf(entity, key), value));
  }
  return parts.length > 0 ? parts.join(' — ') : t.untitled;
}

/** Типи з обрізанням трикрапкою: повне значення — у підказці (LST-06). */
const TOOLTIP_TYPES: ReadonlySet<string> = new Set([
  'text',
  'long_text',
  'url',
  'email',
  'identifier',
  'multipicklist',
]);

const NUMERIC_TYPES: ReadonlySet<string> = new Set([
  'number',
  'integer',
  'decimal',
  'percent',
  'date',
  'datetime',
]);

/** Значення поля-списку (телефон, пошта): основне + «+N» (LST-07). */
function ListValue({ field, value }: { field: MetaField; value: FieldValue }) {
  if (!Array.isArray(value)) return display(field, value);
  const [first, ...rest] = value;
  if (first === undefined) return null;
  const text = typeof first === 'string' ? first : first.value;
  // Маска (SEC-074) — як прийшла з сервера: форматування номера її б зіпсувало.
  const masked = typeof first !== 'string' && first.masked === true;
  const shown = field.type === 'phone' && !masked ? formatPhone(text) : text;
  return (
    <span className="row-flex">
      <span className={field.type === 'phone' ? 'num' : 'truncate'}>{shown}</span>
      {rest.length > 0 && <span className="badge">+{rest.length}</span>}
    </span>
  );
}

/**
 * Комірка за типом поля реєстру (LST-07): код знає типи, а не поля. Поле етапу воронки з `entity` —
 * бейдж у тоні етапу (`stageTone`); `pipeline` — воронка рядка, якщо список її несе.
 */
export function MetaCell({
  field,
  value,
  entity,
  pipeline,
}: {
  field: MetaField;
  value: FieldValue | undefined;
  entity?: MetaEntity | undefined;
  pipeline?: FieldValue | undefined;
}) {
  if (value === undefined || isEmpty(value)) return null;
  if (isLookup(field)) return <LookupCell field={field} value={value} />;
  if (entity && isStageField(field))
    return <StageBadge label={display(field, value)} tone={stageTone(entity, value, pipeline)} />;
  if (field.list) return <ListValue field={field} value={value} />;
  switch (field.type) {
    case 'picklist':
      return <OptionBadge label={display(field, value)} tone={toneOf(field, value)} />;
    case 'multipicklist':
      return Array.isArray(value)
        ? value
            .map((key) =>
              typeof key === 'string'
                ? (field.options?.find((o) => o.key === key)?.label.uk ?? key)
                : key.value,
            )
            .join(', ')
        : display(field, value);
    case 'boolean':
      return value ? t.yes : <span className="muted">{t.no}</span>;
    default:
      return NUMERIC_TYPES.has(field.type) ? (
        <span className="num">{display(field, value)}</span>
      ) : (
        display(field, value)
      );
  }
}

/**
 * Колонки подання: поле-назва — завжди друга колонка після вибору і не ховається (LST-01), навіть
 * якщо подання її не перелічило; поля, яких немає в метаданих (видалене, приховане правами), —
 * без колонки.
 */
export function viewColumns(
  view: MetaView,
  entity: MetaEntity,
  params: ListParams = {},
): ColumnSpec[] {
  const title = entity.titleFields[0] ?? '';
  const chosen = effectiveColumns(view, params);
  // Особистий вибір поля-назви не містить: воно завжди перше (LST-02).
  const rest = params.columns ? chosen.filter((c) => c.field !== title) : chosen;
  const columns = rest.some((c) => c.field === title)
    ? rest
    : [{ field: title, priority: 1 as const }, ...rest];
  const specs = columnSpecs(columns, title, (key) => {
    // Відповідальний у сторінці списку — лише UUID (імен ще немає): стовпця немає, як до появи
    // поля в `/meta` (web-card.md, розд. 5).
    const field = key === 'owner' ? undefined : fieldOf(entity, key);
    return field
      ? { type: field.type, label: field.label.uk, sortable: field.flags.includes('sortable') }
      : undefined;
  });
  // Особисті ширини (LST-03): поле-назва лишається гнучким — воно забирає залишок області.
  const widths = params.widths;
  if (!widths) return specs;
  return specs.map((c) => {
    const width = widths[c.key];
    return c.isTitle || width === undefined ? c : { ...c, width: clampWidth(width) };
  });
}

/**
 * Опис рядків для ядра таблиці. Стабільне, поки ті самі подання й сутність: зміна даних не
 * перемальовує рядки, тож контекст списку для картки (UX-05) береться з `context` у момент переходу.
 */
export function usePlatformListSource(
  view: MetaView,
  entity: MetaEntity,
  context: RefObject<ListContext | undefined>,
  params: ListParams = {},
): ListSource<RecordRow> {
  const navigate = useNavigate();
  const router = useRouter();
  return useMemo<ListSource<RecordRow>>(
    () => ({
      label: view.label.uk,
      columns: viewColumns(view, entity, params),
      // Під пошуком без вибору людини порядок — за релевантністю: стрілки в шапці немає.
      sort: params.q && !params.sort ? undefined : listSort(view, params),
      rowLabel: (row) => rowTitle(entity, row),
      href: (row) => cardHref(entity.key, row.id),
      open: (row) => {
        mark(PERF_MARKS.cardOpenStart);
        void navigate({
          to: '/r/$entity/$recordId',
          params: { entity: entity.key, recordId: row.id },
          state: historyState({ listContext: context.current }),
        });
      },
      cell: (column, row) => {
        if (column.isTitle) return rowTitle(entity, row);
        const field = fieldOf(entity, column.key);
        return field ? (
          <MetaCell
            field={field}
            value={row.values[column.key]}
            entity={entity}
            pipeline={row.values[STAGE_FIELDS.pipeline]}
          />
        ) : null;
      },
      tooltip: (column, row) => {
        const field = fieldOf(entity, column.key);
        const value = row.values[column.key];
        return field && TOOLTIP_TYPES.has(field.type) && !isEmpty(value)
          ? display(field, value)
          : undefined;
      },
      // Лише код картки (лінивий чанк), без `preloadRoute`: той поклав би в кеш роутера збіг без
      // даних зі статусом `success`, і клік відкрив би картку частинами (рев'ю Архітектора #885,
      // дельта M1). Дані наперед тягне лише `J` (UX-33).
      preload: () => void loadRouteCode(router, '/r/$entity/$recordId').catch(() => undefined),
    }),
    [view, entity, navigate, router, context, params],
  );
}
